import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expertReportKeys } from '@/entities/expert-report';
import { API_TAGS } from '@/shared/api';
import {
  addKmContextAttribute,
  addKmEvalNoteSet,
  createKmAssessment,
  createKmEval,
  createKmGroup,
  deleteKmAssessment,
  deleteKmEval,
  deleteKmGroup,
  fetchKmAssessment,
  fetchKmAssessments,
  fetchKmContext,
  fetchKmEval,
  fetchKmEvalNoteSets,
  fetchKmEvals,
  fetchKmGroup,
  fetchKmGroups,
  fetchKmStructure,
  moveKmAssessment,
  moveKmEval,
  moveKmGroup,
  removeKmContextAttribute,
  removeKmEvalNoteSet,
  updateKmAssessment,
  updateKmEval,
  updateKmGroup,
} from './api';
import type { KmContextLevel, KmStructureParams } from './types';

/** Query keys раздела структуры заключения на модели знаний */
export const expertReportKmKeys = {
  all: [API_TAGS.EXPERT_REPORTS, 'km'] as const,
  structures: () => [...expertReportKmKeys.all, 'structure'] as const,
  structure: (params: KmStructureParams) => [...expertReportKmKeys.structures(), { ...params }] as const,
  groups: () => [...expertReportKmKeys.all, 'group'] as const,
  group: (groupId: number) => [...expertReportKmKeys.groups(), groupId] as const,
  groupList: (params: { reportId: number; parentId?: number }) =>
    [...expertReportKmKeys.groups(), 'list', { ...params }] as const,
  assessments: () => [...expertReportKmKeys.all, 'assessment'] as const,
  assessment: (assessmentId: number) => [...expertReportKmKeys.assessments(), assessmentId] as const,
  assessmentList: (params: { reportId: number; groupId?: number }) =>
    [...expertReportKmKeys.assessments(), 'list', { ...params }] as const,
  evals: () => [...expertReportKmKeys.all, 'eval'] as const,
  eval: (evalId: number) => [...expertReportKmKeys.evals(), evalId] as const,
  evalList: (params: { reportId: number; assessmentId?: number }) =>
    [...expertReportKmKeys.evals(), 'list', { ...params }] as const,
  noteSets: (evalId: number) => [...expertReportKmKeys.evals(), evalId, 'note-set'] as const,
  contexts: () => [...expertReportKmKeys.all, 'context'] as const,
  context: (level: KmContextLevel) => [...expertReportKmKeys.contexts(), { ...level }] as const,
};

/**
 * Хук структуры заключения на модели знаний. Структура читается только у заключения
 * в этом режиме, поэтому запрос включается явным признаком со стороны вызывающего.
 */
export const useKmStructure = (params: KmStructureParams, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: expertReportKmKeys.structure(params),
    queryFn: () => fetchKmStructure(params),
    enabled: options?.enabled !== false && params.reportId > 0,
  });

/** Хук групп одного уровня структуры */
export const useKmGroups = (params: { reportId: number; parentId?: number }, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: expertReportKmKeys.groupList(params),
    queryFn: () => fetchKmGroups(params),
    enabled: options?.enabled !== false && params.reportId > 0,
  });

/** Хук карточки одной группы структуры */
export const useKmGroup = (groupId?: number) =>
  useQuery({
    queryKey: groupId ? expertReportKmKeys.group(groupId) : expertReportKmKeys.groups(),
    queryFn: () => fetchKmGroup(groupId!),
    enabled: typeof groupId === 'number' && groupId > 0,
  });

/** Хук единиц оценивания группы либо всего заключения */
export const useKmAssessments = (params: { reportId: number; groupId?: number }, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: expertReportKmKeys.assessmentList(params),
    queryFn: () => fetchKmAssessments(params),
    enabled: options?.enabled !== false && params.reportId > 0,
  });

/** Хук карточки одной единицы оценивания с её определениями оценки */
export const useKmAssessment = (assessmentId?: number) =>
  useQuery({
    queryKey: assessmentId ? expertReportKmKeys.assessment(assessmentId) : expertReportKmKeys.assessments(),
    queryFn: () => fetchKmAssessment(assessmentId!),
    enabled: typeof assessmentId === 'number' && assessmentId > 0,
  });

/** Хук определений оценки единицы либо всего заключения */
export const useKmEvals = (params: { reportId: number; assessmentId?: number }, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: expertReportKmKeys.evalList(params),
    queryFn: () => fetchKmEvals(params),
    enabled: options?.enabled !== false && params.reportId > 0,
  });

/** Хук карточки одного определения оценки */
export const useKmEval = (evalId?: number) =>
  useQuery({
    queryKey: evalId ? expertReportKmKeys.eval(evalId) : expertReportKmKeys.evals(),
    queryFn: () => fetchKmEval(evalId!),
    enabled: typeof evalId === 'number' && evalId > 0,
  });

/** Хук наборов знаний определения оценки */
export const useKmEvalNoteSets = (evalId?: number) =>
  useQuery({
    queryKey: evalId ? expertReportKmKeys.noteSets(evalId) : expertReportKmKeys.evals(),
    queryFn: () => fetchKmEvalNoteSets(evalId!),
    enabled: typeof evalId === 'number' && evalId > 0,
  });

/**
 * Хук контекста оценки уровня заключения: назначенное самому уровню вместе с приходящим сверху.
 * У единицы оценивания это весь состав, с которым её оценит расчёт заключения.
 */
export const useKmContext = (level: KmContextLevel, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: expertReportKmKeys.context(level),
    queryFn: () => fetchKmContext(level),
    enabled: options?.enabled !== false && level.reportId > 0,
  });

/**
 * Правка структуры затрагивает и дерево, и карточки его элементов: шкала единицы
 * пересчитывается по её определениям, а перенос меняет обе задетые ветки. Поэтому
 * после любой мутации раздела обновляется весь его кеш — раздел небольшой, а частичная
 * инвалидация здесь расходится с фактическими связями элементов.
 *
 * Вместе с разделом обновляется и само заключение: его правило расчёта итога написано по
 * элементам структуры, поэтому правка структуры меняет и состояние этого правила.
 */
const useKmMutation = <TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: expertReportKeys.all }),
  });
};

/** Хук создания группы структуры */
export const useCreateKmGroup = () => useKmMutation(createKmGroup);

/** Хук переименования группы и смены признака заголовка */
export const useUpdateKmGroup = () => useKmMutation(updateKmGroup);

/** Хук переноса группы к другому родителю и смены её порядка */
export const useMoveKmGroup = () => useKmMutation(moveKmGroup);

/** Хук удаления группы вместе со всем вложенным */
export const useDeleteKmGroup = () => useKmMutation(deleteKmGroup);

/** Хук создания единицы оценивания */
export const useCreateKmAssessment = () => useKmMutation(createKmAssessment);

/** Хук переименования единицы оценивания */
export const useUpdateKmAssessment = () => useKmMutation(updateKmAssessment);

/** Хук переноса единицы оценивания в другую группу и смены её порядка */
export const useMoveKmAssessment = () => useKmMutation(moveKmAssessment);

/** Хук удаления единицы оценивания */
export const useDeleteKmAssessment = () => useKmMutation(deleteKmAssessment);

/** Хук создания определения оценки */
export const useCreateKmEval = () => useKmMutation(createKmEval);

/** Хук правки названия и текста определения оценки */
export const useUpdateKmEval = () => useKmMutation(updateKmEval);

/** Хук переноса определения оценки в другую единицу и смены его порядка */
export const useMoveKmEval = () => useKmMutation(moveKmEval);

/** Хук удаления определения оценки */
export const useDeleteKmEval = () => useKmMutation(deleteKmEval);

/** Хук привязки набора знаний к определению оценки */
export const useAddKmEvalNoteSet = () => useKmMutation(addKmEvalNoteSet);

/** Хук отвязки набора знаний от определения оценки */
export const useRemoveKmEvalNoteSet = () => useKmMutation(removeKmEvalNoteSet);

/**
 * Хук назначения атрибута контексту оценки уровня заключения. Назначение меняет контекст всего,
 * что стоит в уровне, поэтому, как и правка структуры, обновляет раздел целиком.
 */
export const useAddKmContextAttribute = () => useKmMutation(addKmContextAttribute);

/** Хук снятия атрибута с контекста оценки уровня: приходящее на уровень сверху при этом остаётся */
export const useRemoveKmContextAttribute = () => useKmMutation(removeKmContextAttribute);
